import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ShoppingBag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, Empty, inputCls, PageLoader } from "@/components/kit";
import { ImageUpload } from "@/components/ImageUpload";
import { api, errMsg, gbp, fmtDate } from "@/lib/api";
import { FormDialog, SectionHead, Toggle } from "./common";
import { UpgradeGate } from "@/components/UpgradeGate";

const BLANK = { name: "", description: "", price: "", image_url: "", options: "", stock: "", active: true };
const TONE = { paid: "amber", fulfilled: "green", cancelled: "red", refunded: "red", expired: "red" };

const ProductForm = ({ f, setF, teamId }) => (
  <>
    <Field label="Product name"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Home shirt 2026" data-testid="product-name-input" /></Field>
    <Field label="Description"><textarea className={`${inputCls} h-20`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="product-description-input" /></Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Price (£)"><Input required type="number" min="0.5" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="product-price-input" /></Field>
      <Field label="Stock" hint="Blank = unlimited"><Input type="number" min="0" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} data-testid="product-stock-input" /></Field>
    </div>
    <Field label="Sizes / options" hint="Comma separated, e.g. S, M, L, XL — leave blank if none"><Input value={f.options} onChange={(e) => setF({ ...f, options: e.target.value })} data-testid="product-options-input" /></Field>
    <Field label="Photo"><ImageUpload value={f.image_url} onChange={(v) => setF({ ...f, image_url: v })} teamId={teamId} testid="product-image" /></Field>
    <Toggle checked={f.active} onChange={(v) => setF({ ...f, active: v })} label="Show in shop" testid="product-active-toggle" />
  </>
);

const OrderRow = ({ o, onStatus }) => (
  <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/5 py-3" data-testid={`shop-order-${o.id}`}>
    <div className="min-w-0">
      <p className="font-semibold text-white">{o.name} <span className="text-sm font-normal text-slate-500">{o.email} {o.phone}</span></p>
      <p className="text-sm text-slate-300">{o.items.map((i) => `${i.qty} × ${i.name}${i.option ? ` (${i.option})` : ""}`).join(", ")}</p>
      <p className="text-xs text-slate-500">{fmtDate(o.created_at)} · {o.delivery ? `Delivery${o.shipping_address ? `: ${o.shipping_address}` : ""}` : "Collect from club"}</p>
    </div>
    <div className="flex items-center gap-2">
      <span className="font-mono text-sm text-white">{gbp(o.total)}</span>
      <Pill tone={TONE[o.status]} data-testid={`order-status-${o.id}`}>{o.status}</Pill>
      {o.status === "paid" && <Button size="sm" className="rounded-full font-bold" onClick={() => onStatus(o, "fulfilled")} data-testid={`fulfil-order-${o.id}`}>Mark fulfilled</Button>}
    </div>
  </div>
);

export default function Shop({ teamId, overview, setTab }) {
  if (!overview.features.card_payments) return <UpgradeGate feature="card_payments" setTab={setTab} />;
  return <ShopInner teamId={teamId} setTab={setTab} />;
}

function ShopInner({ teamId, setTab }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(BLANK);
  const [settings, setSettings] = useState(null);
  const load = () => api.get(`/teams/${teamId}/shop`).then((r) => { setD(r.data); setSettings((s) => s || r.data.settings); });
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const body = { ...f, price: Number(f.price), stock: f.stock === "" || f.stock === null ? null : Number(f.stock), options: String(f.options).split(",") };
    try {
      if (editing) await api.put(`/teams/${teamId}/shop/products/${editing.id}`, body);
      else await api.post(`/teams/${teamId}/shop/products`, body);
      toast.success("Product saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try {
      await api.delete(`/teams/${teamId}/shop/products/${p.id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (o, status) => {
    await api.patch(`/teams/${teamId}/shop/orders/${o.id}`, { status });
    toast.success("Order updated");
    load();
  };
  const saveSettings = async () => {
    try {
      await api.patch(`/teams/${teamId}/shop/settings`, { ...settings, shop_delivery_fee: Number(settings.shop_delivery_fee) || 0 });
      toast.success("Shop settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <PageLoader />;

  return (
    <div className="space-y-6">
      <SectionHead title="Club shop" text="Sell kit and merchandise on your fan hub. Payments go straight to your own Stripe account.">
        <Button className="rounded-full font-bold" onClick={() => { setEditing(null); setF(BLANK); setOpen(true); }} data-testid="add-product-btn"><Plus className="mr-1 h-4 w-4" /> Add product</Button>
      </SectionHead>
      {!d.stripe_connected && (
        <Panel className="flex flex-wrap items-center justify-between gap-3 border-amber-500/30" data-testid="shop-stripe-warning">
          <p className="flex items-center gap-2 text-sm text-amber-200"><AlertTriangle className="h-4 w-4" /> Your shop is hidden from fans until you connect your Stripe account.</p>
          <Button size="sm" className="rounded-full font-bold" onClick={() => setTab("stripe")} data-testid="shop-connect-stripe-btn">Connect Stripe</Button>
        </Panel>
      )}
      <Panel className="grid gap-4 sm:grid-cols-[180px_1fr_auto] sm:items-end">
        <Field label="Delivery fee (£)" hint="0 = collection only"><Input type="number" min="0" step="0.01" value={settings.shop_delivery_fee} onChange={(e) => setSettings({ ...settings, shop_delivery_fee: e.target.value })} data-testid="shop-delivery-fee-input" /></Field>
        <Field label="Note for buyers"><Input value={settings.shop_note} onChange={(e) => setSettings({ ...settings, shop_note: e.target.value })} placeholder="e.g. Collect from the clubhouse on matchdays" data-testid="shop-note-input" /></Field>
        <Button variant="outline" className="rounded-full border-white/15 bg-transparent font-bold" onClick={saveSettings} data-testid="shop-settings-save-btn">Save</Button>
      </Panel>
      {!d.products.length && <Empty icon={ShoppingBag} title="No products yet" text="Add shirts, scarves, training kit or matchday tickets." />}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {d.products.map((p) => (
          <Panel key={p.id} className="!p-0 overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[4/3] bg-white/5">{p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" /> : <ShoppingBag className="m-auto h-full w-10 text-slate-600" />}</div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div><p className="font-display text-xl font-bold uppercase text-white">{p.name}</p><p className="font-mono text-sm text-[#CCFF00]">{gbp(p.price)}</p></div>
                <div className="flex">
                  <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setF({ ...p, options: p.options.join(", "), stock: p.stock ?? "" }); setOpen(true); }} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-rose-400" onClick={() => del(p)} data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">{!p.active && <Pill tone="red">Hidden</Pill>}<Pill>{p.stock ?? "∞"} in stock</Pill>{p.options.length > 0 && <Pill>{p.options.join(" · ")}</Pill>}</div>
            </div>
          </Panel>
        ))}
      </div>
      <Panel data-testid="shop-orders-panel">
        <p className="font-display text-xl font-bold uppercase text-white">Orders</p>
        {d.orders.map((o) => <OrderRow key={o.id} o={o} onStatus={setStatus} />)}
        {!d.orders.length && <p className="mt-3 text-sm text-slate-500">No orders yet.</p>}
      </Panel>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit product" : "Add product"} onSubmit={save} testid="product-form"><ProductForm f={f} setF={setF} teamId={teamId} /></FormDialog>
    </div>
  );
}
